import React from 'react';
import {AlertTriangle, Edit, Globe, Loader2, Sparkles} from 'lucide-react';
import {BillingType, BioMatch, BioMatchType, RuleSource} from '../types';
import {formatRuleTypeLabel, requiresRangeInputs} from '../utils';
import BioMatchStatusDropdown from './BioMatchStatusDropdown';

/**
 * Two flavors of "incomplete" for a range BioMatch:
 *   1. Any biomarker rule has no assay_code — that rule will never
 *      match a patient's lab result, so the rec can't fire.
 *   2. Zero recommended assays — even if rules fire, the rec
 *      contributes nothing to the Custom Lab Package.
 * Curated recs only check (1).
 */
function getIncompleteReasons(match: BioMatch): string[] {
    const reasons: string[] = [];
    const missingAssayCount = (match.biomarkers ?? []).filter(
        (b) => (b.rule_source ?? RuleSource.BIOMARKER) === RuleSource.BIOMARKER && !b.assay_code
    ).length;
    if (missingAssayCount > 0) {
        reasons.push(
            `Missing biomarker on ${missingAssayCount} rule${missingAssayCount === 1 ? '' : 's'}`
        );
    }
    if (match.bm_type === BioMatchType.RANGE && (match.recommended_assays?.length ?? 0) === 0) {
        reasons.push('No recommended assays selected');
    }
    return reasons;
}

interface Props {
    items: BioMatch[];
    loading: boolean;
    loadingEditId: number | null;
    deletingIds: Set<number>;
    canManage: boolean;
    /**
     * Super-admin authoring of globals. When true, the list is showing
     * the global library — globals are owned here, so Edit/Delete are
     * enabled and the Global pill is hidden (every row is global).
     */
    globalScope?: boolean;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
    /**
     * Persist a status/billing change. Caller is expected to PUT the
     * full rec body and refresh the list on success.
     */
    onStatusChange: (
        rec: BioMatch,
        next: {enabled: boolean; billing_type: BillingType; employer_coverage_percent: number | null}
    ) => Promise<void>;
}

export const BioMatchList: React.FC<Props> = ({
    items,
    loading,
    loadingEditId,
    deletingIds,
    canManage,
    globalScope,
    onEdit,
    onDelete,
    onStatusChange,
}) => {
    if (loading) {
        return <p className="text-gray-500">Loading BioMatches...</p>;
    }

    if (items.length === 0) {
        return <p className="text-gray-500">No BioMatch options found.</p>;
    }

    const incompleteCount = items.filter((m) => getIncompleteReasons(m).length > 0).length;

    return (
        <div className="space-y-3">
            {incompleteCount > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-mld-warning/20 bg-mld-warning/5 px-3 py-2 text-xs text-mld-warning">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>
                        <strong>{incompleteCount}</strong> BioMatch
                        {incompleteCount === 1 ? '' : 'es'} need attention. Rows with the amber
                        badge below need an admin to complete them — open and re-pick the missing
                        biomarker or add recommended assays.
                    </span>
                </div>
            )}
            {items.map((match) => {
                // Globals are visible to every org but only the
                // super-admin tooling can edit them — disable the
                // row's interactive controls for org admins. When
                // showing the global library to a super-admin, the
                // edit/delete guards are off (they own globals there).
                const isGlobal = match.organization_id == null;
                const isReadOnlyForOrg = isGlobal && !globalScope;
                const incompleteReasons = getIncompleteReasons(match);
                const incompleteTooltip =
                    incompleteReasons.length > 0
                        ? `Needs attention: ${incompleteReasons.join('; ')}`
                        : '';
                return (
                    <div
                        key={match.id}
                        className="rounded-lg border border-mld-panel-border bg-mld-menu-background p-4 transition hover:border-mld-primary/40">
                        <div className="grid items-center gap-4 xl:grid-cols-[200px_1fr_auto_auto]">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-semibold text-mld-text-primary">{match.name}</span>
                                <div className="ml-auto flex items-center gap-1">
                                    {incompleteReasons.length > 0 && (
                                        <span
                                            title={incompleteTooltip}
                                            aria-label={incompleteTooltip}
                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-mld-warning/30 bg-mld-warning/5 text-mld-warning">
                                            <AlertTriangle className="h-3 w-3" />
                                        </span>
                                    )}
                                    {!globalScope &&
                                        (isGlobal ? (
                                            <span
                                                title="Global BioMatch — visible to every organization, managed by MLD super admins"
                                                className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                                                <Globe className="h-2.5 w-2.5" />
                                                Global
                                            </span>
                                        ) : (
                                            <span
                                                title="Custom BioMatch — created and managed by this organization"
                                                className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                                                <Sparkles className="h-2.5 w-2.5" />
                                                Custom
                                            </span>
                                        ))}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {match.biomarkers.map((b, idx) => {
                                    const rangeValue = requiresRangeInputs(b.rule_type)
                                        ? `${b.min_value ?? ''}–${b.max_value ?? ''}`.trim()
                                        : `${b.threshold_value ?? ''}`.trim();
                                    const ruleLabel = [formatRuleTypeLabel(b.rule_type), rangeValue]
                                        .filter(Boolean)
                                        .join(' ');
                                    const genderLabel = b.gender
                                        ? ` • ${b.gender.charAt(0).toUpperCase() + b.gender.slice(1)}`
                                        : '';

                                    return (
                                        <span
                                            key={`${match.id}-${idx}`}
                                            className="inline-flex items-center rounded-full border border-mld-panel-border bg-mld-menu-background px-3 py-1 text-xs font-medium text-mld-text-secondary">
                                            <span className="font-semibold text-mld-text-primary">{b.test_type}</span>
                                            <span className="mx-1 text-mld-muted-text">·</span>
                                            {ruleLabel}
                                            {genderLabel}
                                        </span>
                                    );
                                })}
                            </div>
                            <div className="justify-self-end">
                                {/*
                                  Billing dropdown is enabled for globals
                                  too — admins set the per-org override
                                  here. The Inactive option is hidden for
                                  globals inside the dropdown itself
                                  (globals can't be disabled per org).
                                */}
                                <BioMatchStatusDropdown
                                    rec={match}
                                    onChange={(next) => onStatusChange(match, next)}
                                    disabled={!canManage}
                                />
                            </div>
                            {canManage && (
                                <div className="flex items-center gap-2 justify-self-end">
                                    <button
                                        type="button"
                                        onClick={() => onEdit(match.id)}
                                        disabled={loadingEditId === match.id || isReadOnlyForOrg}
                                        title={isReadOnlyForOrg ? 'Global BioMatches are read-only here' : 'Edit'}
                                        className="inline-flex items-center gap-1 rounded-md bg-mld-warning/10 px-3 py-1 text-xs font-semibold text-mld-warning hover:bg-mld-warning hover:text-white disabled:cursor-not-allowed disabled:opacity-50">
                                        {loadingEditId === match.id ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <Edit className="h-3 w-3" />
                                        )}
                                        <span>{loadingEditId === match.id ? 'Loading...' : 'Edit'}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onDelete(match.id)}
                                        disabled={deletingIds.has(match.id) || isReadOnlyForOrg}
                                        title={
                                            isReadOnlyForOrg
                                                ? 'Global BioMatches cannot be deleted from an org'
                                                : 'Delete'
                                        }
                                        className="inline-flex items-center gap-1 rounded-md bg-mld-danger/5 px-3 py-1 text-xs font-semibold text-mld-danger hover:bg-mld-danger hover:text-white disabled:cursor-not-allowed disabled:opacity-50">
                                        {deletingIds.has(match.id) && <Loader2 className="h-3 w-3 animate-spin" />}
                                        <span>{deletingIds.has(match.id) ? 'Deleting...' : 'Delete'}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
